/**
 * Who this build says it is.
 *
 * The same codebase runs under more than one name — grittranslate.com and
 * translate.truemarch.com are the same app, the same database and the same
 * deploy; they differ in a wordmark and a logo file and nothing else. Keeping
 * the name in one env-fed module rather than typed into five components is what
 * makes that true: a rebranded copy carries NO code diff, so it can track
 * `main` exactly and update itself whenever the original does.
 *
 * `NEXT_PUBLIC_*` is inlined at BUILD time, not read at runtime — each brand has
 * its own checkout, its own `.env` and therefore its own build. Changing the
 * name means rebuilding that copy, which is what its deploy does anyway.
 */

/** Empty string and whitespace both mean "not set" — an empty .env line is a mistake, not a name. */
function fromEnv(value: string | undefined, fallback: string): string {
  const v = (value || '').trim();
  return v || fallback;
}

const name = fromEnv(process.env.NEXT_PUBLIC_BRAND_NAME, 'TrueMarch World');

/**
 * The header wordmark is drawn in two pieces: the first word in the ink colour,
 * the rest in the accent. Splitting on the first space keeps that automatic for
 * any name ("TrueMarch | Kharma AI") so a new
 * brand only has to set one variable. A single-word name simply gets no accent half.
 */
const firstSpace = name.indexOf(' ');
const lead = firstSpace === -1 ? name : name.slice(0, firstSpace);
const accent = firstSpace === -1 ? '' : name.slice(firstSpace + 1);

/**
 * The mark: the TrueMarch monogram, since 2026-10-15, when the site became
 * TrueMarch World (Translate, Camps and Games are its features; the Kharma coin
 * stays the mark of the KHARMA currency, beside every balance). The monogram
 * is white for dark grounds and black for light ones, so this is a real
 * `light` / `dark` pair. Icons: scripts/make-truemarch-icons.py.
 *
 * History, the coin:
 *
 * It used to be chosen from the brand NAME — the production build wore the
 * company's white TrueMarch monogram (and needed a second, inverted file for
 * the light theme, because white on white is nothing), while the development
 * build wore its own plated mark. Two marks for one product, picked by a regex
 * over a display string.
 *
 * Since 2026-10-13 the mark is the Kharma coin the product already shows beside
 * every balance, and since 2026-10-14 the name is Kharma Translate (the site is
 * Kharma; the translator is one product on it). The coin has a real alpha channel and
 * its own colour, so one file sits on a light header and a dark one alike; the
 * `light` / `dark` pair is kept only so BrandMark has nothing to relearn if a
 * future mark does need two. The tab and home-screen icons are cut from the
 * same artwork by scripts/make-kharma-icons.py.
 */
export const LOGO: { readonly dark: string; readonly light: string } = {
  dark: '/truemarch-96.png',
  light: '/truemarch-96-light.png',
};

export const BRAND = {
  /** The product's name, as it appears in prose, the tab title and the manifest. */
  name,
  /** Header wordmark, pre-split: `lead` in ink, `accent` in the accent colour. */
  wordmark: { lead, accent },
} as const;
